import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  CSSProperties,
  useMemo,
} from "react";
import { createEmbeddingContext } from "amazon-quicksight-embedding-sdk";
import { DashboardFrame } from "amazon-quicksight-embedding-sdk/dist/types";
import { Grid, Loader, Tabs } from "@pushpress/shared-components";
import { useQuicksightEmbedUrl } from "../../../apis/quicksightApi.hooks";
import { format, addMonths } from "date-fns";
import { AppContext } from "../../../context/app";
import { CURRENCIES } from "./../../../utils/currencies";
import { getQuickSightCustomProps } from "./utils";
import { changeTimeZone } from "../../../utils/dateUtils";
import useQuery from "../../../utils/query";
import styles from "./QuickSight.module.scss";
import { IReports } from "../../../apis/reportsApi";

interface QuickSightProps {
  report: IReports;
}

export const QuickSight = ({ report }: QuickSightProps) => {
  const { queryParams, setQueryParam } = useQuery();
  const { client } = useContext(AppContext);
  const [embedDashboardLoaded, setEmbedDashboardLoaded] = useState(false);
  const embeddedDashboard = useRef<DashboardFrame>();
  const containerRef = useRef<HTMLDivElement>();
  const quicksightQuery = useQuicksightEmbedUrl(report?.fields?.dashboardId, {
    cacheTime: 0,
  });

  const handleChangeTab = (tabIndex: number) => {
    const tab = report?.fields?.tabs?.[tabIndex];
    if (tab) {
      embeddedDashboard?.current?.setSelectedSheetId?.(tab?.value);
      setQueryParam("tab", tab.key);
    }
  };

  const selectedTab = useMemo(() => {
    const tabParam = queryParams.get("tab");

    if (!tabParam) return 0;

    return report?.fields?.tabs?.findIndex((tab) => tab.key === tabParam) || 0;
  }, [queryParams, report?.fields]);

  useEffect(() => {
    const sheetId = report?.fields?.tabs?.[selectedTab]?.value;
    if (sheetId) {
      embeddedDashboard?.current?.setSelectedSheetId?.(sheetId);
    }
  }, [selectedTab, report?.fields]);

  useEffect(() => {
    async function loadQuickSight() {
      if (
        quicksightQuery.data?.embedUrl &&
        !embedDashboardLoaded &&
        containerRef.current &&
        client &&
        report?.fields
      ) {
        let customProps = {};

        if (
          report?.fields?.customProps &&
          report?.fields?.customProps?.length > 0
        ) {
          customProps = getQuickSightCustomProps(report?.fields?.customProps);
        }

        const dateOnClientTimezone = changeTimeZone(
          new Date(),
          client.timezone,
        );
        const parameters = [
          {
            Name: "subdomain",
            Values: [client.subdomain],
          },
          {
            Name: "CurrencySymbol",
            Values: [CURRENCIES[client?.currencyIso].symbol_native],
          },
          {
            Name: "ClientUUID",
            Values: [client.uuid],
          },
          {
            Name: "CurrencyCode",
            Values: [client.currencyIso],
          },
          {
            Name: "PreviousMonth",
            Values: [format(addMonths(dateOnClientTimezone, -1), "MMM yyyy")],
          },
          {
            Name: "CurrentMonth",
            Values: [format(dateOnClientTimezone, "MMM yyyy")],
          },
          {
            Name: "NextMonth",
            Values: [format(addMonths(dateOnClientTimezone, 1), "MMM yyyy")],
          },
        ];

        let initialSheetId = "";
        if (report?.fields?.tabs?.length) {
          initialSheetId = report.fields.tabs[selectedTab].value;
        }

        const embeddingContext = await createEmbeddingContext();

        const { embedDashboard } = embeddingContext;
        const embeddedDashboardExperience = await embedDashboard(
          {
            url: quicksightQuery.data?.embedUrl,
            container: containerRef.current,
            resizeHeightOnSizeChangedEvent: true,
            height: report?.fields?.height || "AutoFit",
            withIframePlaceholder: true,
          },
          {
            toolbarOptions: {
              reset: false,
            },
            sheetOptions: {
              initialSheetId,
              singleSheet: true,
            },
            locale: "en-US",
            parameters,
            ...customProps,
          },
        );
        embeddedDashboard.current = embeddedDashboardExperience;
        setEmbedDashboardLoaded(true);
      }
    }

    loadQuickSight();
  }, [
    quicksightQuery.data?.embedUrl,
    setEmbedDashboardLoaded,
    embedDashboardLoaded,
    client,
    report?.fields,
  ]);

  if (quicksightQuery.isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item>
          <Loader />
        </Grid>
      </Grid>
    );
  }

  const customStyle: CSSProperties = {};

  if (report?.fields?.height) {
    customStyle.height = report?.fields?.height;
  }

  return (
    <>
      {report?.fields?.tabs && report?.fields?.tabs?.length > 0 && (
        <div className={styles.tabsContainer}>
          <Tabs
            tabHeaders={report.fields.tabs.map((tab) => tab.key)}
            selectedIndex={selectedTab}
            changeTab={handleChangeTab}
            onTabClick={handleChangeTab}
          />
        </div>
      )}
      <div
        ref={containerRef}
        className={styles.embedContainer}
        style={customStyle}
      />
    </>
  );
};

export default QuickSight;
